import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Phone, Calendar, MapPin, Plus, Loader2, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { searchPatients } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { debounce } from 'lodash';

interface Patient {
  id: string;
  name: string;
  phone?: string;
  age?: number;
  gender?: string;
  address?: string;
  email?: string;
  last_visit?: string;
  registration_date?: string;
}

interface PatientSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientSelect: (patient: Patient) => void;
  title?: string;
  description?: string;
  allowCreateNew?: boolean;
  onCreateNew?: () => void;
  context?: 'appointment' | 'invoice' | 'general';
}

export default function PatientSearchDialog({
  isOpen,
  onClose,
  onPatientSelect,
  title = "Select Patient",
  description,
  allowCreateNew = true,
  onCreateNew,
  context = 'general'
}: PatientSearchDialogProps) {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Auto-generate description based on context
  const defaultDescription = description || (() => {
    switch (context) {
      case 'appointment':
        return "Search and select a patient to schedule an appointment";
      case 'invoice':
        return "Search and select a patient to create an invoice";
      default:
        return "Search and select a patient from the database";
    }
  })();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setPatients([]);
      setIsSearching(false);
      setHasSearched(false);
      setError(null);
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      if (term.length < 2) {
        setPatients([]);
        setHasSearched(false);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        setError(null);
        
        const results = await searchPatients(term, currentUser?.clinic_id);
        
        // Handle different API response formats
        let patientsArray = [];
        if (Array.isArray(results)) {
          patientsArray = results;
        } else if (results && typeof results === 'object' && 'patients' in results && Array.isArray((results as any).patients)) {
          patientsArray = (results as any).patients;
        } else if (results && typeof results === 'object' && 'data' in results && Array.isArray((results as any).data)) {
          patientsArray = (results as any).data;
        } else {
          console.warn('Unexpected search API response format:', results);
          patientsArray = [];
        }
        
        setPatients(patientsArray);
        setHasSearched(true);
      } catch (error) {
        console.error('Failed to search patients:', error);
        setError('Failed to search patients. Please try again.');
        setPatients([]);
        setHasSearched(true);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  // Trigger search when search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
    setSelectedIndex(-1); // Reset selection when search changes
    
    // Cleanup function to cancel debounced calls
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (patients.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < patients.length - 1 ? prev + 1 : 0;
          // Scroll selected item into view
          setTimeout(() => {
            const element = document.querySelector(`[data-patient-index="${newIndex}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : patients.length - 1;
          // Scroll selected item into view
          setTimeout(() => {
            const element = document.querySelector(`[data-patient-index="${newIndex}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < patients.length) {
          handlePatientSelect(patients[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    onPatientSelect(patient);
  };

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
    } else {
      // Default behavior - close dialog and let parent handle
      onClose();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getGenderBadgeColor = (gender?: string) => {
    switch (gender?.toLowerCase()) {
      case 'male':
      case 'm':
        return 'bg-info/10 text-info';
      case 'female':
      case 'f':
        return 'bg-medical-vitals/10 text-medical-vitals';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
          {defaultDescription && (
            <p className="text-sm text-muted-foreground mt-1">{defaultDescription}</p>
          )}
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Search Section */}
          <div className="p-4 border-b flex-shrink-0">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Type at least 2 characters to search by name, phone, or MRN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 text-base"
                autoFocus
                onKeyDown={handleKeyDown}
              />
              {isSearching && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 flex-shrink-0">
              <Alert className="status-critical">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {!hasSearched && !isSearching && (
              <div className="p-8 text-center">
                <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Search for Patients</h3>
                <p className="text-muted-foreground">
                  Start typing a patient's name, phone number, or email to search
                </p>
              </div>
            )}

            {isSearching && (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasSearched && !isSearching && patients.length === 0 && !error && (
              <div className="p-8 text-center">
                <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No patients found</h3>
                <p className="text-muted-foreground mb-4">
                  No patients match your search criteria: "{searchTerm}"
                </p>
                {allowCreateNew && (
                  <Button onClick={handleCreateNew} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Create New Patient
                  </Button>
                )}
              </div>
            )}

            {patients.length > 0 && (
              <div className="p-4 space-y-2">
                <div className="text-sm text-muted-foreground mb-3">
                  Found {patients.length} patient{patients.length !== 1 ? 's' : ''}
                </div>
                
                {patients.map((patient, index) => (
                  <div
                    key={patient.id}
                    data-patient-index={index}
                    onClick={() => handlePatientSelect(patient)}
                    className={cn(
                      "p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md",
                      selectedIndex === index
                        ? "border-primary bg-primary/5 shadow-sm ring-2 ring-primary/20"
                        : "border-border hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        
                        {/* Patient Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground truncate">
                              {patient.name}
                            </h3>
                            {patient.gender && (
                              <Badge className={cn("text-xs px-2 py-0.5", getGenderBadgeColor(patient.gender))}>
                                {patient.gender === 'M' || patient.gender === 'Male' ? 'Male' : 
                                 patient.gender === 'F' || patient.gender === 'Female' ? 'Female' : 
                                 patient.gender}
                              </Badge>
                            )}
                            {patient.age && (
                              <Badge variant="outline" className="text-xs px-2 py-0.5">
                                Age {patient.age}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                            {patient.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span>{patient.phone}</span>
                              </div>
                            )}
                            
                            {patient.last_visit && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>Last visit: {formatDate(patient.last_visit)}</span>
                              </div>
                            )}
                            
                            {patient.address && (
                              <div className="flex items-center gap-1 col-span-full">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{patient.address}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="border-t p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              {/* Help text with keyboard shortcuts */}
              <div className="text-xs text-muted-foreground">
                {patients.length > 0 ? (
                  <span>Use ↑↓ arrows to navigate, Enter to select, Esc to close</span>
                ) : (
                  <span>Start typing to search for patients...</span>
                )}
              </div>
              
              <div className="flex gap-2">
                {allowCreateNew && (
                  <Button 
                    variant="outline" 
                    onClick={handleCreateNew}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Patient
                  </Button>
                )}
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}